import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type { Dayjs } from "dayjs";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import { dayjs } from "@/core/utils/date";
import {
  payPeriodDetailPrefixKey,
  payPeriodsKey,
} from "@/payroll/pay-periods/query";
import type { EnrichedPayPeriod } from "@/payroll/payroll.d";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import {
  applyOptimisticBulkPaste,
  clearOptimisticBulkPasteRows,
  rollbackOptimisticBulkPaste,
} from "./bulk-paste-cache";
import { cellCommentThreadsKey } from "./cell-comments/query";
import {
  followAiScriptExplanationChatTurn,
  runAiScriptExplanationChatTurn,
} from "./explanation-chat-transport";
import { enqueueSheetLayoutMutation } from "./sheet-layout-mutation-queue";
import {
  addOptimisticManualRows,
  buildOptimisticCell,
  findSheetResponseCell,
  patchSpreadsheetSheetCells,
  patchSpreadsheetSheetLayout,
  reconcileCreatedManualRowCells,
  removeManualRowCells,
} from "./spreadsheet-cache";
import {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetListKey,
  aiScriptSpreadsheetSheetQueryOptions,
  cancelChangedSpreadsheetSheetWarmup,
  getAiScriptSpreadsheetSheetPath,
  refreshChangedSpreadsheetSheets,
  startChangedSpreadsheetSheetWarmup,
} from "./spreadsheet-sheet-query";
import type { ExplanationChatPathBuilder } from "./types";
import type {
  WorkbookExportOptions,
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IAcceptAiScriptCellOverrideResponse,
  IAiScriptCellOverridesResponse,
  IAiScriptOverrideCandidatesResponse,
  IAiScriptExplanationChatThread,
  IAiScriptExplanationChatThreadResponse,
  IAiScriptExplanationChatThreadsResponse,
  ICreateAiScriptRunResponse,
  IAiSpreadsheetCell,
  IAiSpreadsheetResponse,
  IAiSpreadsheetSheetResponse,
  IManualRowCellInput,
  IManualRowInput,
  IManualRowsResponse,
  IAiSpreadsheetPasteEdit,
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionInput,
  IAiSpreadsheetRegionOverrideCandidate,
  IAiSpreadsheetRegionOverrideConfirmResponse,
  IAiSpreadsheetRegionOverrideReview,
  IAiSpreadsheetRegionResponse,
  IUpdateAiSpreadsheetSheetResponse,
  IAiScriptStatementResponse,
} from "./types";
import {
  applyCreatedAiScriptRunToPayPeriods,
  createdAiScriptRunsByPeriodKey,
  isAiScriptWorkbookAuthorizationError,
  type CreatedAiScriptRunByPeriodStart,
} from "./utils";
import {
  cancelWorkbookCellEdits,
  enqueueWorkbookCellEdit,
  flushWorkbookCellEdits,
  hasPendingWorkbookCellEdits,
  WorkbookCellEditBatchError,
} from "./workbook-cell-edit-batcher";
import {
  requestAiScriptWorkbookExport,
  validateWorkbookExportOptions,
  WORKBOOK_EXPORT_STATUS,
} from "./workbook-export";
import {
  cancelQueuedWorkbookMutations,
  executeQueuedWorkbookMutation,
  isRecoverableWorkbookOperationError,
  type WorkbookMutationRefreshContext,
  type WorkbookRefreshMode,
  workbookMutationRequestConfig,
} from "./workbook-mutation";
import {
  completeWorkbookOperation,
  recoverWorkbookOperation,
  setWorkbookOperationRecoveryError,
  useWorkbookOperationSnapshot,
  WorkbookOperationFailedError,
} from "./workbook-operation";
import type { WorkbookReadSource } from "./workbook-read-source";

const invalidateOptimisticSheetOnMutationError = (
  queryClient: QueryClient,
  error: unknown,
  context:
    | {
        sheetKey?: ReturnType<typeof aiScriptSpreadsheetSheetKey>;
      }
    | undefined,
) => {
  if (isRecoverableWorkbookOperationError(error) || !context?.sheetKey) return;

  void queryClient.invalidateQueries({ queryKey: context.sheetKey });
};

interface CreateAiScriptRunParams {
  startDate: string;
  endDate: string;
  // Optional explicit versions for a re-run. Omit both to reproduce today's
  // behavior (published script version + published roster). "Reproduce last
  // run" sends the prior run's stored ids; "Run with latest" omits them.
  aiScriptVersionId?: string | null;
  payrollRosterId?: string | null;
  // Rerun only these tabs, carrying every other tab forward from the previous
  // run. Omit for a full-workbook run. The script still executes in full — only
  // the write is narrowed — so the rest of the workbook keeps the numbers that
  // were already reviewed.
  sheetNames?: string[] | null;
}

interface DownloadAiScriptWorkbookParams extends WorkbookExportOptions {
  runId: string;
  filename: string;
}

interface UpdateAiSpreadsheetCellParamsBase {
  runId: string;
  sheetId: string;
  cellId: string;
  rawInput: string;
  // Explicit formula/value intent for manual-row cells. Omitted by format-only
  // changes so they preserve the cell's current kind.
  kind?: AiSpreadsheetCellKind;
}

interface UpdateAiSpreadsheetCellParams
  extends UpdateAiSpreadsheetCellParamsBase {
  // Optional explicit cell format ("Format cell" menu). Omitted → the cell
  // keeps its current type.
  valueType?: AiSpreadsheetValueType;
  // The workbook edit sequence this mutation is based on, sent by undo/redo so
  // a stale replay is rejected (409) instead of clobbering a newer edit. Other
  // direct edits omit it (unconditional write).
  baseVersion?: number;
}

export const buildUpdateAiSpreadsheetCellBody = ({
  rawInput,
  kind,
  valueType,
  baseVersion,
}: Pick<
  UpdateAiSpreadsheetCellParams,
  "rawInput" | "kind" | "valueType" | "baseVersion"
>) => ({
  raw_input: rawInput,
  ...(kind ? { kind } : {}),
  // Only send value_type when reformatting; omitting it keeps the type.
  ...(valueType ? { value_type: valueType } : {}),
  // Only sent by undo/redo; a live edit is an unconditional write.
  ...(baseVersion != null ? { base_version: baseVersion } : {}),
});

interface UpdateAiSpreadsheetSheetBaseParams {
  runId: string;
  sheetId: string;
}

export type UpdateAiSpreadsheetSheetParams =
  UpdateAiSpreadsheetSheetBaseParams &
    (
      | { columnWidths: Record<string, number> }
      | { rowHeights: Record<string, number> }
      | { frozenColumn: number | null }
    ) & {
      columnWidths?: Record<string, number>;
      rowHeights?: Record<string, number>;
      frozenColumn?: number | null;
    };

export interface CreateAiSpreadsheetRegionParams {
  runId: string;
  sheetId: string;
  region: IAiSpreadsheetRegionInput;
}

export interface UpdateAiSpreadsheetRegionParams
  extends CreateAiSpreadsheetRegionParams {
  regionId: string;
}

export interface DeleteAiSpreadsheetRegionParams {
  runId: string;
  sheetId: string;
  regionId: string;
}

export interface ConfirmAiSpreadsheetRegionOverrideParams {
  runId: string;
  // The override is addressed by id alone; sheetId only routes the cache update
  // to the sheet whose review list the override was surfaced on.
  sheetId: string;
  overrideId: string;
  candidate: IAiSpreadsheetRegionOverrideCandidate;
}

const SHEET_LAYOUT_REQUEST_TIMEOUT_MS = 30_000;

export interface BulkPasteAiSpreadsheetParams {
  runId: string;
  sheetId: string;
  edits: IAiSpreadsheetPasteEdit[];
  rows: IManualRowInput[];
  // Sent by undo/redo replays so a paste against a workbook that moved underneath
  // returns 409 (the forward paste omits it).
  baseVersion?: number;
}

interface CreateAiScriptExplanationChatParams {
  runId: string;
  message: string;
  threadId?: string | null;
  threadName?: string | null;
  onThread?: (thread: IAiScriptExplanationChatThread, turnId: string) => void;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

interface FollowAiScriptExplanationChatParams {
  runId: string;
  threadId: string;
  turnId: string;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

/**
 * Builds the chat root for one subject. The explanation-chat hooks are written
 * against this shape rather than a fixed URL so other surfaces (profitability
 * reports) can reuse the same transport, thread list and streaming.
 */
export const getAiScriptExplanationChatPath: ExplanationChatPathBuilder = (
  businessId,
  runId,
) => `/businesses/${businessId}/ai_script_runs/${runId}/explanation_chat`;

export const getAiScriptSpreadsheetPath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet`;

/** The caller's own pack. Takes no technician: the server knows who is asking. */
export const getAiScriptStatementPath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/statement`;

/** One named technician's pack, for somebody answering questions about it. */
export const getAiScriptTechnicianStatementPath = (
  businessId: string,
  runId: string,
  technicianId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/statements/${technicianId}`;

export const getAiScriptSpreadsheetCellPath = (
  businessId: string,
  runId: string,
  cellId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/cells/${cellId}`;

export const getAiScriptSpreadsheetPastePath = (
  businessId: string,
  runId: string,
) => `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/paste`;

export const getAiScriptSpreadsheetRegionsPath = (
  businessId: string,
  runId: string,
  sheetId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/sheets/${sheetId}/regions`;

export const getAiScriptSpreadsheetRegionPath = (
  businessId: string,
  runId: string,
  sheetId: string,
  regionId: string,
) =>
  `${getAiScriptSpreadsheetRegionsPath(businessId, runId, sheetId)}/${regionId}`;

export const getAiScriptSpreadsheetRegionOverrideConfirmPath = (
  businessId: string,
  runId: string,
  overrideId: string,
) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/region_overrides/${overrideId}/confirm`;

export const postAiScriptSpreadsheetPaste = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, edits, rows, baseVersion }: BulkPasteAiSpreadsheetParams,
  idempotencyKey: string = crypto.randomUUID(),
) =>
  api
    .post(
      getAiScriptSpreadsheetPastePath(businessId, runId),
      {
        sheet_id: sheetId,
        edits,
        rows,
        base_version: baseVersion,
      },
      workbookMutationRequestConfig(idempotencyKey),
    )
    .then((res) => res.data as AcceptedAiSpreadsheetOperationResponse);

export const aiScriptSpreadsheetKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-spreadsheet", businessId, runId] as const;

export const aiScriptSpreadsheetQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  runId: string | null,
) => ({
  queryKey: aiScriptSpreadsheetKey(businessId, runId),
  queryFn: ({ signal }: { signal: AbortSignal }) => {
    if (!businessId || !runId) throw new Error("Payroll run is required");

    return api
      .get(getAiScriptSpreadsheetPath(businessId, runId), { signal })
      .then((res) => res.data as IAiSpreadsheetResponse);
  },
  retry: false,
});

export {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetQueryOptions,
  getAiScriptSpreadsheetSheetPath,
} from "./spreadsheet-sheet-query";

/**
 * Everything that reads the workbook's numbers without being the workbook.
 *
 * Any write to a cell can move all of these, so both refresh paths invalidate
 * the whole list — the fast one for typing, the full one for structural
 * operations. Listing them once is the point: these were duplicated by hand,
 * and a per-technician pack preview got added to one path and missed in the
 * other, which left the PackChat rail and the PayPacks sidebar showing pre-edit
 * pay until a reload. A new derived view belongs here and nowhere else.
 */
export const derivedWorkbookViewKeys = (businessId: string, runId: string) => [
  aiScriptCellOverridesKey(businessId, runId),
  aiScriptCellOverrideHistoryPrefixKey(businessId, runId),
  // A manual row is not just structure: editing one of its cells rewrites the
  // row's seed and stamps its `edited_by`/`edited_at`, which is what the edits
  // panel renders as "Last edited by". Without this, an ordinary grid edit left
  // an open panel showing the row as never edited until a full reload.
  aiScriptManualRowsKey(businessId, runId),
  // By prefix, so it reaches every pack under this run: the technician's own and
  // each one a manager has open in the PackChat rail or the figure picker. The
  // exact key ends in a technician id, so anything narrower than this misses them.
  aiScriptStatementPrefixKey(businessId, runId),
  // Sheet approvals are pinned to a fingerprint of the workbook's contents, so
  // ANY edit silently stops an existing sign-off from counting — the server
  // recomputes that on read, and without this the strip would keep showing a
  // green mark for an approval the API already considers withdrawn. Invalidated
  // by prefix rather than by period: these mutations know the run, not the pay
  // period, and every period detail is cheap to re-read.
  payPeriodDetailPrefixKey(),
];

const refreshWorkbookAfterOperation = async (
  queryClient: QueryClient,
  businessId: string,
  runId: string,
) => {
  // A structural operation can add, remove or rename sheets and rows, so the
  // workbook's own shape goes stale alongside everything derived from it.
  await Promise.all(
    [
      aiScriptSpreadsheetKey(businessId, runId),
      aiScriptSpreadsheetSheetListKey(businessId, runId),
      ...derivedWorkbookViewKeys(businessId, runId),
      // Only the server re-places comment threads, and rows moving is exactly
      // what moves them. A plain cell edit leaves rows where they are, so the
      // fast path does not need this.
      cellCommentThreadsKey(businessId, runId),
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
};

// Exported for tests: the fast path a plain grid edit takes, kept honest about
// which caches an edit can move.
export const refreshWorkbookAfterCellEdits = async (
  queryClient: QueryClient,
  businessId: string,
  runId: string,
  { immediateSheetId, changedSheetIds }: WorkbookMutationRefreshContext,
) => {
  await refreshChangedSpreadsheetSheets({
    queryClient,
    businessId,
    runId,
    changedSheetIds,
    immediateSheetId,
  });

  // The derived views can change after a cell edit, but they do not block the
  // visible sheet from completing, so none of this is awaited. The workbook's
  // shape queries stay valid because an ordinary edit does not touch them.
  for (const queryKey of derivedWorkbookViewKeys(businessId, runId)) {
    void queryClient.invalidateQueries({ queryKey });
  }
};

const executeWorkbookMutation = async ({
  api,
  queryClient,
  businessId,
  runId,
  submit,
  refreshMode = "full",
  immediateSheetId,
  flushPendingCellEdits = true,
  hasPendingClientBatch,
}: {
  api: AxiosInstance;
  queryClient: QueryClient;
  businessId: string;
  runId: string;
  submit: (
    idempotencyKey: string,
  ) => Promise<AcceptedAiSpreadsheetOperationResponse>;
  refreshMode?: WorkbookRefreshMode;
  immediateSheetId?: string;
  flushPendingCellEdits?: boolean;
  hasPendingClientBatch?: () => boolean;
}) => {
  if (flushPendingCellEdits) {
    await flushWorkbookCellEdits(businessId, runId);
  }

  return executeQueuedWorkbookMutation({
    api,
    businessId,
    runId,
    submit,
    refreshMode,
    immediateSheetId,
    hasPendingClientBatch,
    onQueueDrained: async (context) => {
      if (!context.requiresFullRefresh) {
        await refreshWorkbookAfterCellEdits(
          queryClient,
          businessId,
          runId,
          context,
        );
        return;
      }

      await refreshWorkbookAfterOperation(queryClient, businessId, runId);
      void startChangedSpreadsheetSheetWarmup({
        api,
        queryClient,
        businessId,
        runId,
        sheetIds: context.changedSheetIds,
      });
    },
  });
};

export const useRecoverAiScriptWorkbookOperation = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const snapshot = useWorkbookOperationSnapshot(businessId, runId);

  useEffect(() => {
    if (!businessId || !runId) return;
    let active = true;
    const controller = new AbortController();

    const recover = async () => {
      try {
        const result = await recoverWorkbookOperation(
          api,
          businessId,
          runId,
          controller.signal,
        );
        if (!active) return;
        if (!result) {
          completeWorkbookOperation(businessId, runId);
          return;
        }
        await refreshWorkbookAfterOperation(queryClient, businessId, runId);
        if (active) completeWorkbookOperation(businessId, runId);
      } catch (error) {
        if (!active || error instanceof WorkbookOperationFailedError) return;

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Grid recalculation status could not be refreshed.";
        setWorkbookOperationRecoveryError(
          businessId,
          runId,
          errorMessage,
          () => {
            void recover();
          },
        );
      }
    };

    void recover();
    return () => {
      active = false;
      controller.abort();
    };
  }, [api, businessId, queryClient, runId]);

  useEffect(
    () => () => {
      if (businessId && runId) {
        cancelQueuedWorkbookMutations(businessId, runId);
        cancelWorkbookCellEdits(businessId, runId);
        cancelChangedSpreadsheetSheetWarmup(businessId, runId);
      }
    },
    [businessId, runId],
  );

  return snapshot;
};

export const useGetAiScriptSpreadsheet = (
  businessId: string | null,
  runId: string | null,
  source?: WorkbookReadSource,
) => {
  const api = useApi();
  return useQuery<IAiSpreadsheetResponse>({
    ...(source
      ? {
          queryKey: [source.cacheKey, businessId, runId, "workbook"],
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            api
              .get(source.workbookPath, { signal })
              .then((res) => res.data as IAiSpreadsheetResponse),
          retry: false,
        }
      : aiScriptSpreadsheetQueryOptions(api, businessId, runId)),
    enabled: !!businessId && !!runId,
  });
};

export const useLoadAiScriptSpreadsheetSheet = (
  businessId: string | null,
  runId: string | null,
  sheetId: string | null,
  shouldLoad: boolean,
  source?: WorkbookReadSource,
) => {
  const api = useApi();
  return useQuery<IAiSpreadsheetSheetResponse>({
    ...(source
      ? {
          queryKey: [source.cacheKey, businessId, runId, "sheet", sheetId],
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            api
              .get(`${source.workbookPath}/sheets/${sheetId}`, { signal })
              .then((res) => res.data as IAiSpreadsheetSheetResponse),
          retry: false,
          staleTime: Infinity,
        }
      : aiScriptSpreadsheetSheetQueryOptions(api, businessId, runId, sheetId)),
    enabled: !!businessId && !!runId && !!sheetId && shouldLoad,
  });
};

/**
 * Whose pack the statement endpoint should resolve.
 *
 * `viewer` reads the caller's own pack, from a route that takes no technician and
 * so cannot be aimed at anyone else. `technician` names a pack in the path, for
 * somebody answering questions about it; that route refuses technicians outright.
 *
 * Two routes, one query and one cache root, distinguished only by the id in the
 * key. That last part matters: keys match by prefix, so invalidating a run reaches
 * every pack under it. When these were separate roots, one of them could be — and
 * was — invalidated while the other went on serving pre-edit pay. Sharing a URL was
 * never what made them one query; being the same resource is.
 */
export const STATEMENT_IDENTITY = {
  viewer: "viewer",
  technician: "technician",
} as const;

export type StatementIdentity =
  (typeof STATEMENT_IDENTITY)[keyof typeof STATEMENT_IDENTITY];

/**
 * Naming a technician is required when the identity is `technician`, so a missing id
 * is a type error rather than a call that silently falls back to the caller's own
 * pack — a manager would be shown their own empty statement in place of the
 * technician's.
 */
type StatementIdentityParams =
  | { identity: typeof STATEMENT_IDENTITY.viewer; technicianId?: never }
  | {
      identity: typeof STATEMENT_IDENTITY.technician;
      technicianId: string | null;
    };

export const aiScriptStatementKey = (
  businessId: string | null,
  runId: string | null,
  technicianId: string | null = null,
) => ["ai-script-statement", businessId, runId, technicianId] as const;

/**
 * Every pack for one run, the viewer's own included.
 *
 * A workbook edit changes whichever packs that cell feeds, and the caller does not
 * know whose — so an edit invalidates the run's packs as a set rather than guessing
 * at a technician id.
 */
export const aiScriptStatementPrefixKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-statement", businessId, runId] as const;

/**
 * One technician's read-time pay statement, projected server-side from the
 * workbook's anchors (see statement-schema.ts).
 *
 * Two routes serve it: one for the caller's own pack, one for a named technician's.
 * They are the same resource read by different people, so they are one query under
 * one cache root, and `identity` picks the route.
 */
export const useGetAiScriptStatement = (
  businessId: string | null,
  runId: string | null,
  {
    identity,
    technicianId,
    enabled,
  }: StatementIdentityParams & { enabled: boolean },
) => {
  const api = useApi();
  const named = identity === STATEMENT_IDENTITY.technician;
  const requestedId = named ? (technicianId ?? null) : null;

  const query = useQuery({
    queryKey: aiScriptStatementKey(businessId, runId, requestedId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(
          requestedId
            ? getAiScriptTechnicianStatementPath(businessId, runId, requestedId)
            : getAiScriptStatementPath(businessId, runId),
        )
        .then((res) => res.data as IAiScriptStatementResponse);
    },
    // A named identity waits for its id: without one this would call the caller's
    // own route and show a manager their own statement instead of the technician's.
    enabled: enabled && !!businessId && !!runId && (!named || !!requestedId),
    retry: false,
    ...(named
      ? {
          staleTime: 0,
          refetchOnMount: "always" as const,
          refetchInterval: 30_000,
        }
      : {
          // A published period is read live, so a technician's own statement is
          // never treated as fresh: coming back to the tab or reopening it refetches.
          // No polling: a technician with the page open reloads if told to.
          // Without this the app-wide five-minute staleTime skipped that refetch.
          staleTime: 0,
        }),
  });
  return named ? currentPayrollResult(query) : query;
};

const optimisticallyUpdateAiSpreadsheetCell = async (
  queryClient: QueryClient,
  businessId: string | null,
  {
    runId,
    sheetId,
    cellId,
    rawInput,
    kind,
    valueType,
  }: UpdateAiSpreadsheetCellParams,
) => {
  const sheetListKey = aiScriptSpreadsheetSheetListKey(businessId, runId);

  await queryClient.cancelQueries({ queryKey: sheetListKey });

  const loadedSheets = queryClient.getQueriesData<IAiSpreadsheetSheetResponse>({
    queryKey: sheetListKey,
  });
  const existingCell = loadedSheets.reduce<IAiSpreadsheetCell | undefined>(
    (found, [, data]) => found ?? findSheetResponseCell(data, cellId),
    undefined,
  );

  if (existingCell) {
    const optimisticCell = buildOptimisticCell(
      existingCell,
      rawInput,
      valueType,
      kind,
    );
    queryClient.setQueriesData<IAiSpreadsheetSheetResponse>(
      { queryKey: sheetListKey },
      (data) => patchSpreadsheetSheetCells(data, [optimisticCell]),
    );
  }

  return {
    runId,
    sheetKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
  };
};

const handleAiSpreadsheetCellUpdateError = (
  queryClient: QueryClient,
  businessId: string | null,
  error: unknown,
  context:
    | Awaited<ReturnType<typeof optimisticallyUpdateAiSpreadsheetCell>>
    | undefined,
) => {
  const operationError =
    error instanceof WorkbookCellEditBatchError ? error.cause : error;
  if (isRecoverableWorkbookOperationError(operationError)) return;
  if (!businessId || !context) return;

  void flushWorkbookCellEdits(businessId, context.runId).then(() =>
    queryClient.invalidateQueries({
      queryKey: context.sheetKey,
      refetchType: "all",
    }),
  );
};

// Regular grid edits always enter the short client queue. The queue sends one
// bulk request for all edits that arrive in its batch window.
export const useUpdateAiSpreadsheetCell = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: ({
      runId,
      sheetId,
      cellId,
      rawInput,
      kind,
    }: UpdateAiSpreadsheetCellParamsBase) => {
      if (!businessId) throw new Error("Business is required");

      return enqueueWorkbookCellEdit({
        businessId,
        runId,
        sheetId,
        edit: {
          cell_id: cellId,
          raw_input: rawInput,
          ...(kind ? { kind } : {}),
        },
        execute: (batchSheetId, edits) =>
          executeWorkbookMutation({
            api,
            queryClient,
            businessId,
            runId,
            refreshMode: "cell-edit",
            immediateSheetId: batchSheetId,
            flushPendingCellEdits: false,
            hasPendingClientBatch: () =>
              hasPendingWorkbookCellEdits(businessId, runId),
            submit: (idempotencyKey) =>
              postAiScriptSpreadsheetPaste(
                api,
                businessId,
                {
                  runId,
                  sheetId: batchSheetId,
                  edits,
                  rows: [],
                },
                idempotencyKey,
              ),
          }),
        report: (metrics) =>
          posthog?.capture("workbook_cell_edit_batch_completed", {
            batch_size: metrics.batchSize,
            operation_duration_ms: Math.round(metrics.operationDurationMs),
            queue_duration_ms: Math.round(metrics.queueDurationMs),
            succeeded: metrics.succeeded,
          }),
        onError: (error) =>
          handleAiSpreadsheetCellUpdateError(queryClient, businessId, error, {
            runId,
            sheetKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
          }),
      });
    },
    onMutate: (params) =>
      optimisticallyUpdateAiSpreadsheetCell(queryClient, businessId, params),
  });
};

// Ordered format and history operations use the direct cell endpoint because
// they must keep value-type and base-version semantics.
export const useDirectUpdateAiSpreadsheetCell = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      cellId,
      rawInput,
      kind,
      valueType,
      baseVersion,
    }: UpdateAiSpreadsheetCellParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = buildUpdateAiSpreadsheetCellBody({
        rawInput,
        kind,
        valueType,
        baseVersion,
      });
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .patch(
              getAiScriptSpreadsheetCellPath(businessId, runId, cellId),
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onMutate: (params) =>
      optimisticallyUpdateAiSpreadsheetCell(queryClient, businessId, params),
    onError: (error, _variables, context) =>
      handleAiSpreadsheetCellUpdateError(
        queryClient,
        businessId,
        error,
        context,
      ),
  });
};

export const useUpdateAiSpreadsheetColumnValueType = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      runId: string;
      sheetId: string;
      col: number;
      valueType: AiSpreadsheetValueType;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId: params.runId,
        immediateSheetId: params.sheetId,
        submit: (idempotencyKey) =>
          api
            .patch(
              `${getAiScriptSpreadsheetSheetPath(businessId, params.runId, params.sheetId)}/columns/${params.col}`,
              {
                value_type: params.valueType,
                ...(params.baseVersion != null
                  ? { base_version: params.baseVersion }
                  : {}),
              },
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onSuccess: (result, params) => {
      // Refresh unopened sheets too. Query errors must not undo mutation success.
      for (const sheetId of result.changed_sheet_ids ?? []) {
        void queryClient.prefetchQuery(
          aiScriptSpreadsheetSheetQueryOptions(
            api,
            businessId,
            params.runId,
            sheetId,
          ),
        );
      }
    },
  });
};

export const useBulkPasteAiSpreadsheet = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: BulkPasteAiSpreadsheetParams) => {
      if (!businessId) throw new Error("Business is required");

      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId: params.runId,
        submit: (idempotencyKey) =>
          postAiScriptSpreadsheetPaste(api, businessId, params, idempotencyKey),
      });
    },
    // Paint the pasted values immediately so the grid doesn't sit frozen for the
    // (multi-second, large-book) atomic recalc round-trip. Existing-cell edits
    // patch in place by id; new rows drop in as optimistic synthetic-id cells
    // scoped by a token. The final queued mutation triggers an authoritative
    // refetch; onError rolls this paste back when the request itself was rejected.
    onMutate: ({ runId, sheetId, edits, rows }) =>
      applyOptimisticBulkPaste({
        queryClient,
        businessId,
        runId,
        sheetId,
        edits,
        rows,
      }),
    onError: (error, _variables, context) => {
      if (isRecoverableWorkbookOperationError(error)) return;
      // Scoped, version-aware rollback: a failed paste persisted nothing, so
      // restore the cells it patched (only where they still hold this paste's
      // optimistic value) and drop its optimistic row placeholders. Edits the
      // user made to other cells — or to these cells since — are left intact.
      if (context) rollbackOptimisticBulkPaste(queryClient, context);
    },
    onSuccess: (_response, _variables, context) => {
      // Remove optimistic row placeholders scoped to this paste. The queued
      // batch's final mutation owns the authoritative workbook refresh.
      if (context) clearOptimisticBulkPasteRows(queryClient, context);
    },
  });
};

export const patchAiSpreadsheetSheetLayout = (
  api: AxiosInstance,
  businessId: string,
  params: UpdateAiSpreadsheetSheetParams,
) => {
  const body = {
    ...(params.columnWidths !== undefined && {
      column_widths: params.columnWidths,
    }),
    ...(params.rowHeights !== undefined && { row_heights: params.rowHeights }),
    ...(params.frozenColumn !== undefined && {
      frozen_column: params.frozenColumn,
    }),
  };

  return api
    .patch(
      getAiScriptSpreadsheetSheetPath(businessId, params.runId, params.sheetId),
      body,
      { timeout: SHEET_LAYOUT_REQUEST_TIMEOUT_MS },
    )
    .then((res) => res.data as IUpdateAiSpreadsheetSheetResponse);
};

export const useUpdateAiSpreadsheetSheet = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAiSpreadsheetSheetParams) => {
      if (!businessId) throw new Error("Business is required");

      return enqueueSheetLayoutMutation({
        businessId,
        runId: params.runId,
        sheetId: params.sheetId,
        execute: () => patchAiSpreadsheetSheetLayout(api, businessId, params),
      });
    },
    onSuccess: (response, { runId, sheetId }) => {
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
        aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
        (data) => patchSpreadsheetSheetLayout(data, response.sheet),
      );
    },
  });
};

export const patchAiSpreadsheetRegionCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  update: (regions: IAiSpreadsheetRegion[]) => IAiSpreadsheetRegion[],
) => {
  queryClient.setQueryData<IAiSpreadsheetResponse>(
    aiScriptSpreadsheetKey(businessId, runId),
    (data) =>
      data
        ? {
            ...data,
            spreadsheet: {
              ...data.spreadsheet,
              sheets: data.spreadsheet.sheets.map((sheet) =>
                sheet.id === sheetId
                  ? { ...sheet, regions: update(sheet.regions ?? []) }
                  : sheet,
              ),
            },
          }
        : data,
  );
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    (data) =>
      data
        ? {
            ...data,
            sheet: {
              ...data.sheet,
              regions: update(data.sheet.regions ?? []),
            },
          }
        : data,
  );
};

export const invalidateAiSpreadsheetRegionCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: aiScriptSpreadsheetKey(businessId, runId),
    }),
    queryClient.invalidateQueries({
      queryKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    }),
  ]);

// A confirmed override drops out of both the sheet-level review list and the
// workbook-level roll-up, which are populated independently by the API.
export const patchAiSpreadsheetRegionOverrideReviewCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  update: (
    reviews: IAiSpreadsheetRegionOverrideReview[],
  ) => IAiSpreadsheetRegionOverrideReview[],
) => {
  queryClient.setQueryData<IAiSpreadsheetResponse>(
    aiScriptSpreadsheetKey(businessId, runId),
    (data) =>
      data
        ? {
            ...data,
            spreadsheet: {
              ...data.spreadsheet,
              region_override_reviews: update(
                data.spreadsheet.region_override_reviews ?? [],
              ),
              sheets: data.spreadsheet.sheets.map((sheet) =>
                sheet.id === sheetId
                  ? {
                      ...sheet,
                      region_override_reviews: update(
                        sheet.region_override_reviews ?? [],
                      ),
                    }
                  : sheet,
              ),
            },
          }
        : data,
  );
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    (data) =>
      data
        ? {
            ...data,
            sheet: {
              ...data.sheet,
              region_override_reviews: update(
                data.sheet.region_override_reviews ?? [],
              ),
            },
          }
        : data,
  );
};

export const syncConfirmedAiSpreadsheetRegionOverrideCaches = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
  sheetId: string,
  overrideId: string,
  region: IAiSpreadsheetRegion | null,
) => {
  patchAiSpreadsheetRegionOverrideReviewCaches(
    queryClient,
    businessId,
    runId,
    sheetId,
    (reviews) => reviews.filter((review) => review.id !== overrideId),
  );

  if (region) {
    patchAiSpreadsheetRegionCaches(
      queryClient,
      businessId,
      runId,
      sheetId,
      (regions) => [
        ...regions.filter((current) => current.id !== region.id),
        region,
      ],
    );
    return Promise.resolve();
  }

  // A null region is a confirmed deletion tombstone. The response does not
  // include the id of the generated region that the API removed, so reload both
  // region owners instead of leaving that deleted table in the UI.
  return invalidateAiSpreadsheetRegionCaches(
    queryClient,
    businessId,
    runId,
    sheetId,
  );
};

export const postAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, region }: CreateAiSpreadsheetRegionParams,
) =>
  api
    .post(getAiScriptSpreadsheetRegionsPath(businessId, runId, sheetId), {
      confirmed: true,
      region,
    })
    .then((res) => res.data as IAiSpreadsheetRegionResponse);

export const patchAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, regionId, region }: UpdateAiSpreadsheetRegionParams,
) =>
  api
    .patch(
      getAiScriptSpreadsheetRegionPath(businessId, runId, sheetId, regionId),
      { region },
    )
    .then((res) => res.data as IAiSpreadsheetRegionResponse);

export const deleteAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, regionId }: DeleteAiSpreadsheetRegionParams,
) =>
  api.delete(
    getAiScriptSpreadsheetRegionPath(businessId, runId, sheetId, regionId),
  );

export const useCreateAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      return postAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: ({ region }, { runId, sheetId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) => [...regions, region],
      );
    },
  });
};

export const useUpdateAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      return patchAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: ({ region }, { runId, sheetId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) =>
          regions.map((current) =>
            current.id === region.id ? region : current,
          ),
      );
    },
  });
};

export const useDeleteAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      await deleteAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: (_response, { runId, sheetId, regionId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) => regions.filter((region) => region.id !== regionId),
      );
    },
  });
};

export const postAiScriptSpreadsheetRegionOverrideConfirm = (
  api: AxiosInstance,
  businessId: string,
  { runId, overrideId, candidate }: ConfirmAiSpreadsheetRegionOverrideParams,
) =>
  api
    .post(
      getAiScriptSpreadsheetRegionOverrideConfirmPath(
        businessId,
        runId,
        overrideId,
      ),
      // The endpoint permits only these two; end_col is derived from the saved
      // header width, so sending it back would be an unpermitted parameter.
      {
        candidate: {
          header_row: candidate.header_row,
          start_col: candidate.start_col,
        },
      },
    )
    .then((res) => res.data as IAiSpreadsheetRegionOverrideConfirmResponse);

/**
 * Resolves a parked region override. The response carries the region that was
 * applied (null when the override is a deletion tombstone) plus the override's
 * new outcome, so the sheet's review list and its regions both move in one step.
 */
export const useConfirmAiSpreadsheetRegionOverride = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ConfirmAiSpreadsheetRegionOverrideParams) => {
      if (!businessId) throw new Error("Business is required");
      return postAiScriptSpreadsheetRegionOverrideConfirm(
        api,
        businessId,
        params,
      );
    },
    onSuccess: ({ region }, { runId, sheetId, overrideId }) =>
      syncConfirmedAiSpreadsheetRegionOverrideCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        overrideId,
        region,
      ),
  });
};

// ---- Persistent-edit overrides -------------------------------------------

export const getAiScriptCellOverridesPath = (
  businessId: string,
  runId: string,
) => `/businesses/${businessId}/ai_script_runs/${runId}/cell_overrides`;

export const aiScriptCellOverridesKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-cell-overrides", businessId, runId] as const;

const refreshOverrideAccess = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: aiScriptCellOverridesKey(businessId, runId),
  });
  queryClient.invalidateQueries({
    queryKey: aiScriptSpreadsheetKey(businessId, runId),
  });
};

// The persistent edits tracked for this run's period (applied / needs-review /
// dropped), for the review UI. Per-cell badges read `cell.override` from the
// sheet payload; this list backs the review + dropped-edits panels.
export const useGetAiScriptCellOverrides = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptCellOverridesKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(getAiScriptCellOverridesPath(businessId, runId))
        .then((res) => res.data as IAiScriptCellOverridesResponse);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

export const aiScriptCellOverrideHistoryPrefixKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-cell-override-history", businessId, runId] as const;

export const aiScriptCellOverrideHistoryKey = (
  businessId: string | null,
  runId: string | null,
  overrideKey: string | null,
) =>
  [
    ...aiScriptCellOverrideHistoryPrefixKey(businessId, runId),
    overrideKey,
  ] as const;

// The full edit history (current + superseded versions) for one cell's override
// key — backs the focused per-cell view.
export const useGetAiScriptCellOverrideHistory = (
  businessId: string | null,
  runId: string | null,
  overrideKey: string | null,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptCellOverrideHistoryKey(businessId, runId, overrideKey),
    queryFn: () => {
      if (!businessId || !runId || !overrideKey) {
        throw new Error("Override key is required");
      }

      return api
        .get(getAiScriptCellOverridesPath(businessId, runId), {
          params: { override_key: overrideKey },
        })
        .then((res) => res.data as IAiScriptCellOverridesResponse);
    },
    enabled: !!businessId && !!runId && !!overrideKey,
    retry: false,
  });
};

// Accept a flagged edit: keep re-applying it, clear the needs-review flag.
export const useAcceptAiScriptCellOverride = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
    }: {
      runId: string;
      overrideId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post(
          `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/accept`,
        )
        .then((res) => res.data as IAcceptAiScriptCellOverrideResponse);
    },
    onSuccess: (_response, { runId }) => {
      queryClient.invalidateQueries({
        queryKey: aiScriptCellOverridesKey(businessId, runId),
      });
      queryClient.invalidateQueries({
        queryKey: aiScriptCellOverrideHistoryPrefixKey(businessId, runId),
      });
      // Re-fetch sheet cells so the cell's `override` block reflects the cleared flag.
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetSheetListKey(businessId, runId),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

// Dismiss an edit: stop re-applying it and revert the current cell to the
// generated value. The authoritative refetch supplies recalculated cells.
export const useDismissAiScriptCellOverride = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
    }: {
      runId: string;
      overrideId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .delete(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}`,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

// The candidate rows an ambiguous edit could belong to (for the review picker).
export const aiScriptOverrideCandidatesKey = (
  businessId: string | null,
  runId: string | null,
  overrideId: string | null,
) => ["ai-script-override-candidates", businessId, runId, overrideId] as const;

export const useGetAiScriptOverrideCandidates = (
  businessId: string | null,
  runId: string | null,
  overrideId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptOverrideCandidatesKey(businessId, runId, overrideId),
    queryFn: () => {
      if (!businessId || !runId || !overrideId) {
        throw new Error("Override is required");
      }

      return api
        .get(
          `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/candidates`,
        )
        .then((res) => res.data as IAiScriptOverrideCandidatesResponse);
    },
    enabled: enabled && !!businessId && !!runId && !!overrideId,
    retry: false,
  });
};

// Bind an ambiguous edit to the row the reviewer picked (by cell id).
export const useDisambiguateAiScriptCellOverride = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
      cellId,
    }: {
      runId: string;
      overrideId: string;
      cellId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = { cell_id: cellId };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/disambiguate`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

// Restore a superseded version of a cell's edit: make the chosen prior version
// the current override again and revert the cell to it. `overrideId` is the
// cell's current override; `targetVersionId` is the superseded version (same
// override key) to bring back. The authoritative refetch supplies recalculated
// cells.
export const useRestoreOverrideVersion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
      targetVersionId,
      baseVersion,
    }: {
      runId: string;
      overrideId: string;
      targetVersionId: string;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        target_version_id: targetVersionId,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

// Discard every manual edit on the run: revert each edited cell to the script's
// value and remove every manually-added row. The final queue refetch reconciles
// reverted cells, removed rows, and per-tech totals.
export const useDiscardAllEdits = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      baseVersion,
    }: {
      runId: string;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = baseVersion != null ? { base_version: baseVersion } : {};
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/discard_all`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

// ---- Manual (user-added) rows --------------------------------------------

export const getAiScriptManualRowsPath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/manual_rows`;

export const aiScriptManualRowsKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-manual-rows", businessId, runId] as const;

interface CreateManualRowParams {
  runId: string;
  sheetId: string;
  // A single row's seed cells, or an explicit list of rows (a paste).
  cells?: Record<string, IManualRowCellInput>;
  rows?: IManualRowInput[];
  anchor?: { after_row?: number };
  // Workbook edit sequence for a redo replay; omitted for a live add.
  baseVersion?: number;
  // Client-only identity for the optimistic placeholder row. Callers that need
  // to buffer edits can use the same token to reconcile them after creation.
  optimisticToken?: string;
}

export const canBufferOptimisticManualRowEdits = (
  optimisticToken: string | undefined,
  rowInputCount: number,
): boolean => optimisticToken != null && rowInputCount === 1;

// The user-added rows tracked for this run's period (for the edits panel).
export const useGetAiScriptManualRows = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptManualRowsKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(getAiScriptManualRowsPath(businessId, runId))
        .then((res) => res.data as IManualRowsResponse);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

// Add one or more rows to a sheet. Optimistic placeholders render immediately;
// the final queue refetch replaces them and reconciles rows shifted by an insert.
export const useCreateManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      sheetId,
      cells,
      rows,
      anchor,
      baseVersion,
      optimisticToken: _optimisticToken,
    }: CreateManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const versionField =
        baseVersion != null ? { base_version: baseVersion } : {};
      const body = rows
        ? { sheet_id: sheetId, rows, ...versionField }
        : { sheet_id: sheetId, cells, anchor, ...versionField };

      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              getAiScriptManualRowsPath(businessId, runId),
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    // Show the new row immediately so it doesn't blink in after the round-trip
    // — for both an append (at the end) and an insert-between (open a gap by
    // shifting rows below the anchor down one, then drop the row into it). The
    // optimistic cells carry synthetic ids; the final queue refetch replaces
    // them with authoritative ids and row numbers, including shifted rows.
    onMutate: async ({
      runId,
      sheetId,
      cells,
      rows,
      anchor,
      optimisticToken,
    }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      if (!queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey)) {
        return {};
      }

      const rowInputs = rows ?? (cells ? [{ cells }] : []);
      // Insert-between: the row lands at afterRow + 1 (1-based), after the gap.
      // Append: it lands just past the last content row.
      const afterRow = anchor?.after_row ?? null;
      // Scope this mutation's optimistic ids to itself so a concurrent add's
      // onSuccess can't strip this one's still-pending optimistic row.
      const token = optimisticToken ?? crypto.randomUUID();

      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        addOptimisticManualRows(data, {
          sheetId,
          rowInputs,
          token,
          afterRow,
          editableWhilePending: canBufferOptimisticManualRowEdits(
            optimisticToken,
            rowInputs.length,
          ),
        }),
      );

      return { sheetKey, optimisticToken: token };
    },
    onError: (error, _variables, context) => {
      if (!isRecoverableWorkbookOperationError(error) && context?.sheetKey) {
        queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
          context.sheetKey,
          (data) =>
            reconcileCreatedManualRowCells(data, [], context.optimisticToken),
        );
      }
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
    onSuccess: (_response, { runId, sheetId }, context) => {
      // The authoritative sheet refetch has landed; strip only this mutation's
      // optimistic placeholders if they survived a cache race.
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
        aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
        (data) =>
          reconcileCreatedManualRowCells(data, [], context?.optimisticToken),
      );
    },
  });
};

interface DeleteManualRowParams {
  runId: string;
  sheetId: string;
  manualRowId: string;
  // Workbook edit sequence for a redo replay; omitted for a live delete.
  baseVersion?: number;
}

// Remove a user-added row. Optimistically drops its cells from the sheet cache
// so it disappears immediately; the follow-up re-fetch reconciles the rows that
// shift up to close the gap and the per-tech totals that recompute server-side.
export const useDeleteManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowId,
      baseVersion,
    }: DeleteManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const config = {
        ...(baseVersion != null
          ? { params: { base_version: baseVersion } }
          : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .delete(
              `${getAiScriptManualRowsPath(businessId, runId)}/${manualRowId}`,
              {
                ...config,
                ...workbookMutationRequestConfig(idempotencyKey),
              },
            )
            .then((res) => res.data),
      });
    },
    onMutate: async ({ runId, sheetId, manualRowId }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        removeManualRowCells(data, manualRowId),
      );

      return { sheetKey };
    },
    onError: (error, _variables, context) => {
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
  });
};

interface RestoreManualRowParams {
  runId: string;
  sheetId: string;
  manualRowId: string;
  // Workbook edit sequence for an undo replay; omitted for a live restore.
  baseVersion?: number;
}

// Restore a previously deleted manual row (the inverse of a delete, used by
// undo). Structural like create/delete, so the final queue refetch reconciles
// the same caches and supplies the re-materialized rows and cells.
export const useRestoreManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowId,
      baseVersion,
    }: RestoreManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = baseVersion != null ? { base_version: baseVersion } : {};
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/${manualRowId}/restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
  });
};

interface BulkManualRowsParams {
  runId: string;
  sheetId: string;
  manualRowIds: string[];
  // Workbook edit sequence for an undo/redo replay.
  baseVersion?: number;
}

// Delete many manual rows in ONE request — the undo of a paste that spilled past
// the last data row and created rows. Reconciles the same caches as a single
// delete; the follow-up re-fetch resolves the shifted rows and recomputed totals.
export const useBulkDeleteManualRows = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowIds,
      baseVersion,
    }: BulkManualRowsParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        manual_row_ids: manualRowIds,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/bulk_destroy`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    // Optimistically drop every removed row's cells so undoing a paste that
    // created rows clears them immediately, not after the settle refetch.
    onMutate: async ({ runId, sheetId, manualRowIds }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        manualRowIds.reduce((acc, id) => removeManualRowCells(acc, id), data),
      );
      return { sheetKey };
    },
    onError: (error, _variables, context) => {
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
  });
};

// Restore many previously-deleted manual rows in ONE request — the redo of a
// bulk-paste undo. Each row returns with its original identity (see the API's
// bulk_restore); reconciles the same caches as a single restore.
export const useBulkRestoreManualRows = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowIds,
      baseVersion,
    }: BulkManualRowsParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        manual_row_ids: manualRowIds,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/bulk_restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
  });
};

export const useCreateAiScriptRun = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      startDate,
      endDate,
      aiScriptVersionId,
      payrollRosterId,
      sheetNames,
    }: CreateAiScriptRunParams) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post(`/businesses/${businessId}/ai_script_runs`, {
          start_date: startDate,
          end_date: endDate,
          ...(aiScriptVersionId
            ? { ai_script_version_id: aiScriptVersionId }
            : {}),
          ...(payrollRosterId ? { payroll_roster_id: payrollRosterId } : {}),
          ...(sheetNames?.length ? { sheet_names: sheetNames } : {}),
        })
        .then((res) => res.data as ICreateAiScriptRunResponse);
    },
    onSuccess: (response, { startDate, endDate }) => {
      const createdRun = { startDate, endDate, run: response.run };

      queryClient.setQueryData<CreatedAiScriptRunByPeriodStart>(
        createdAiScriptRunsByPeriodKey(businessId),
        (runs) => ({
          ...runs,
          [startDate]: createdRun,
        }),
      );
      queryClient.setQueryData<EnrichedPayPeriod[]>(
        payPeriodsKey(businessId),
        (periods) => applyCreatedAiScriptRunToPayPeriods(periods, createdRun),
      );
      queryClient.invalidateQueries({ queryKey: payPeriodsKey(businessId) });
    },
  });
};

/**
 * The API renders the .xlsx in a background job rather than inside the request —
 * the largest workbooks have hundreds of sheets and used to time out the proxy.
 * So a download is a poll: 202 while the build is in flight, 200 with the file
 * once it is cached. Cached workbooks come back on the first request.
 */
const WORKBOOK_EXPORT_FIRST_POLL_DELAY = 750;
const WORKBOOK_EXPORT_POLL_DELAY = 2500;
const WORKBOOK_EXPORT_TIMEOUT = 4 * 60 * 1000;
/**
 * A long build is ~100 polls, so a single dropped request (a 502 from a rolling
 * deploy, a tunnel, a refresh blip) must not end the download. Retries are
 * bounded per poll rather than infinite so a genuinely broken request still
 * surfaces, and the deadline is the real backstop either way.
 */
const WORKBOOK_EXPORT_POLL_RETRIES = 3;

export const WORKBOOK_EXPORT_TIMEOUT_MESSAGE =
  "This workbook is taking longer than expected to prepare. Please try again in a few minutes.";

export const aiScriptWorkbookExportKey = (
  businessId: string | null,
  runId: string | null,
  attempt: number,
) => ["ai-script-workbook-export", businessId, runId, attempt] as const;

export const hasWorkbookExportExpired = (startedAt: Dayjs) =>
  dayjs().diff(startedAt, "milliseconds") >= WORKBOOK_EXPORT_TIMEOUT;

/**
 * Retrying these buys nothing: the answer will not change, so the only effect is
 * making the user wait out the backoff before seeing the real message — and a
 * repeated 401 re-enters the token-refresh interceptor each time. Retries are for
 * the dropped request the poll loop is designed to survive, not for a verdict.
 */
const WORKBOOK_EXPORT_TERMINAL_STATUSES = [401, 403, 404, 422, 500];

export const isTerminalExportError = (error: unknown) =>
  error instanceof WorkbookCellEditBatchError ||
  (error instanceof ApiError &&
    WORKBOOK_EXPORT_TERMINAL_STATUSES.includes(error.status));

/**
 * A cached workbook usually lands on the first retry, so the first poll is quick
 * and the rest back off. The delay is capped to whatever is left of the deadline
 * so the poll that reports the timeout actually runs rather than being scheduled
 * past it — hence the 1ms floor, which React Query needs to keep the interval on.
 */
export const getWorkbookExportPollDelay = (
  completedPolls: number,
  elapsedMs: number,
) => {
  const delay =
    completedPolls <= 1
      ? WORKBOOK_EXPORT_FIRST_POLL_DELAY
      : WORKBOOK_EXPORT_POLL_DELAY;

  return Math.max(1, Math.min(delay, WORKBOOK_EXPORT_TIMEOUT - elapsedMs));
};

interface WorkbookExportRequest extends DownloadAiScriptWorkbookParams {
  attempt: number;
  startedAt: Dayjs;
  /**
   * The business the attempt was started for. Carried on the request rather than
   * read live, because the global tenant selector can swap the selection without
   * unmounting the page — and a run id and claim token only mean anything to the
   * business they came from.
   */
  businessId: string;
}

const saveWorkbookFile = (file: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

/**
 * Downloading is a query rather than a fire-and-forget promise so React Query
 * owns the poll: `refetchInterval` drives it while the API answers 202, and the
 * query signal aborts the in-flight request when the page unmounts instead of
 * dropping a file onto whatever screen the user moved to. Each click is its own
 * attempt (and its own key), so nothing is ever served from cache.
 */
export const useDownloadAiScriptWorkbook = (businessId: string | null) => {
  const api = useApi();
  const attempts = useRef(0);
  const settledAttempt = useRef(0);
  const tokenRef = useRef<string | undefined>(undefined);
  const [request, setRequest] = useState<WorkbookExportRequest | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isComplete, setIsComplete] = useState(false);
  const startedAt = request?.startedAt;

  // Compared during render, not in an effect: on a business change React commits the
  // new businessId with the old request still in state, and an effect that cleared it
  // afterwards would already have let the query fire once against the wrong business.
  const isCurrentBusiness = !!request && request.businessId === businessId;

  const { data, error: queryError } = useQuery({
    queryKey: aiScriptWorkbookExportKey(
      request?.businessId ?? businessId,
      request?.runId ?? null,
      request?.attempt ?? 0,
    ),
    enabled: !!businessId && isCurrentBusiness,
    queryFn: async ({ signal }) => {
      if (!request) throw new Error("Business is required");
      // Cell edits include their server calculation. Drain them before reading
      // an export so a cached file cannot win the race against an active save.
      await flushWorkbookCellEdits(request.businessId, request.runId, {
        rejectOnError: true,
      });
      signal.throwIfAborted();
      // Checked before every poll, not just after a 202, so a build that never
      // lands cannot start another request past the deadline.
      if (hasWorkbookExportExpired(request.startedAt)) {
        throw new Error(WORKBOOK_EXPORT_TIMEOUT_MESSAGE);
      }

      return requestAiScriptWorkbookExport(
        api,
        request.businessId,
        request.runId,
        signal,
        tokenRef.current,
        request,
      );
    },
    refetchInterval: (query) => {
      // Deliberately keeps polling after a failed poll: `retry` absorbs a blip,
      // and the deadline check in `queryFn` is what ends a build that never
      // lands. Stopping on the first error is what let one dropped request abort
      // an otherwise healthy download.
      if (!startedAt || hasWorkbookExportExpired(startedAt)) return false;
      if (
        query.state.data?.status === WORKBOOK_EXPORT_STATUS.ready ||
        isTerminalExportError(query.state.error) ||
        query.state.error?.message === WORKBOOK_EXPORT_TIMEOUT_MESSAGE
      ) {
        return false;
      }

      return getWorkbookExportPollDelay(
        query.state.dataUpdateCount,
        dayjs().diff(startedAt, "milliseconds"),
      );
    },
    // People switch tabs while a big workbook builds; the poll has to survive it.
    refetchIntervalInBackground: true,
    // The result is a one-shot side effect, never a cached read.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      error instanceof Error &&
      error.message !== WORKBOOK_EXPORT_TIMEOUT_MESSAGE &&
      !isTerminalExportError(error) &&
      failureCount < WORKBOOK_EXPORT_POLL_RETRIES,
    // A paused query (offline) never runs `queryFn`, so without this the deadline
    // below is the only thing that can end the attempt.
    networkMode: "always",
  });

  useEffect(() => {
    if (data?.status === WORKBOOK_EXPORT_STATUS.processing && data.token) {
      tokenRef.current = data.token;
    }
  }, [data]);

  // The deadline has to live out here as well as in `queryFn`: React Query pauses
  // a query it cannot run, and a paused poll never reaches `queryFn`, so the
  // button would spin forever with no toast and no way back.
  useEffect(() => {
    if (!request) return undefined;

    const remaining = Math.max(
      0,
      WORKBOOK_EXPORT_TIMEOUT - dayjs().diff(request.startedAt, "milliseconds"),
    );
    const timer = setTimeout(() => {
      if (settledAttempt.current === request.attempt) return;

      settledAttempt.current = request.attempt;
      setError(new Error(WORKBOOK_EXPORT_TIMEOUT_MESSAGE));
      setRequest(null);
    }, remaining);

    return () => clearTimeout(timer);
  }, [request]);

  useEffect(() => {
    if (!request || settledAttempt.current === request.attempt) return;

    if (data?.status === WORKBOOK_EXPORT_STATUS.ready) {
      settledAttempt.current = request.attempt;
      saveWorkbookFile(data.file, data.filename ?? request.filename);
      setIsComplete(true);
      setRequest(null);
      return;
    }

    if (queryError) {
      settledAttempt.current = request.attempt;
      setError(queryError);
      setRequest(null);
    }
  }, [data, queryError, request]);

  // `isCurrentBusiness` has already stopped the query from running; this clears the
  // abandoned attempt so the button stops spinning and no stale error is left behind
  // on a screen that now belongs to a different tenant.
  useEffect(() => {
    if (!request || isCurrentBusiness) return;

    tokenRef.current = undefined;
    settledAttempt.current = request.attempt;
    setRequest(null);
    setError(null);
    setIsComplete(false);
  }, [request, isCurrentBusiness]);

  const download = useCallback(
    (params: DownloadAiScriptWorkbookParams) => {
      if (!businessId) return;

      setIsComplete(false);
      try {
        validateWorkbookExportOptions(params);
      } catch (validationError) {
        setError(validationError);
        return;
      }
      attempts.current += 1;
      tokenRef.current = undefined;
      setError(null);
      setRequest({
        ...params,
        sheetIds: params.sheetIds ? [...params.sheetIds] : undefined,
        attempt: attempts.current,
        startedAt: dayjs(),
        businessId,
      });
    },
    [businessId],
  );

  return {
    download,
    /** True from the click until the file is saved or the attempt fails. */
    isDownloading: !!request,
    /** True only once the API has answered 202 — a cached workbook never sets it. */
    isPreparing: data?.status === WORKBOOK_EXPORT_STATUS.processing,
    error,
    isComplete,
  };
};

const AI_SCRIPT_EXPLANATION_CHAT_THREADS_KEY =
  "ai-script-explanation-chat-threads";
const AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY =
  "ai-script-explanation-chat-thread";

/**
 * Keyed by the chat root rather than the ids alone: the root already contains
 * both, and it keeps two surfaces chatting about the same subject apart.
 */
const chatRoot = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder,
) => (businessId && runId ? buildPath(businessId, runId) : null);

export const aiScriptExplanationChatThreadsKey = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREADS_KEY,
    chatRoot(businessId, runId, buildPath),
  ] as const;

export const isChatAccessDeniedError = (error: unknown) =>
  error instanceof ApiError && error.status === 403;

export const useGetAiScriptExplanationChatThreads = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptExplanationChatThreadsKey(businessId, runId, buildPath),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(`${buildPath(businessId, runId)}/threads`)
        .then((res) => res.data as IAiScriptExplanationChatThreadsResponse);
    },
    enabled: !!businessId && !!runId,
    // 403 means the user lacks workbook visibility; retrying won't change it.
    retry: (failureCount, error) =>
      !isChatAccessDeniedError(error) && failureCount < 2,
  });
};

export const aiScriptExplanationChatThreadKey = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY,
    chatRoot(businessId, runId, buildPath),
    threadId,
  ] as const;

const aiScriptExplanationChatThreadListKey = (
  businessId: string | null,
  runId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) =>
  [
    AI_SCRIPT_EXPLANATION_CHAT_THREAD_KEY,
    chatRoot(businessId, runId, buildPath),
  ] as const;

export const useGetAiScriptExplanationChatThread = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptExplanationChatThreadKey(
      businessId,
      runId,
      threadId,
      buildPath,
    ),
    queryFn: () => {
      if (!businessId || !runId || !threadId)
        throw new Error("Thread is required");

      return api
        .get(`${buildPath(businessId, runId)}/threads/${threadId}`)
        .then((res) => res.data as IAiScriptExplanationChatThreadResponse);
    },
    enabled: !!businessId && !!runId && !!threadId,
  });
};

// Submits one chat turn, then follows its live stream (same poll/cursor
// transport as the onboarding harness) until the final payload arrives.
// History is persisted server-side per thread.
export const useCreateAiScriptExplanationChat = (
  businessId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      runId,
      message,
      threadId,
      threadName,
      onThread,
      onTextDelta,
      onProgress,
    }: CreateAiScriptExplanationChatParams) => {
      if (!businessId) throw new Error("Business is required");

      return runAiScriptExplanationChatTurn({
        api,
        chatPath: buildPath(businessId, runId),
        message,
        threadId,
        threadName,
        onThread,
        onTextDelta,
        onProgress,
      });
    },
    onSettled: (_result, _error, { runId }) => {
      // Refresh the thread list (ordering, auto-renamed default threads) and
      // any cached thread transcripts after each turn.
      queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadsKey(
          businessId,
          runId,
          buildPath,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadListKey(
          businessId,
          runId,
          buildPath,
        ),
      });
    },
  });
};

export const useFollowAiScriptExplanationChatTurn = (
  businessId: string | null,
  buildPath: ExplanationChatPathBuilder = getAiScriptExplanationChatPath,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      runId,
      threadId,
      turnId,
      onTextDelta,
      onProgress,
    }: FollowAiScriptExplanationChatParams) => {
      if (!businessId) throw new Error("Business is required");

      const response = await followAiScriptExplanationChatTurn({
        api,
        chatPath: buildPath(businessId, runId),
        turnId,
        onTextDelta,
        onProgress,
      });

      return { response, threadId, turnId };
    },
    onSettled: (_result, _error, { runId }) => {
      queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadsKey(
          businessId,
          runId,
          buildPath,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: aiScriptExplanationChatThreadListKey(
          businessId,
          runId,
          buildPath,
        ),
      });
    },
  });
};
